// The judge calibration API (cloud/specs/api_contracts.md §11.6; TOM-141), for
// the Labels pages. Kept beside the Evals API rather than in adminApi.ts so the
// pages that use it import only what they need.
//
// No call here reads a verdict before a label exists: the item read answers
// verdict null until this admin has labelled the item, and the only other way
// a verdict reaches the page is the answer to saving a label.

import { adminApi } from '../adminApi';
import {
  agreementQuery,
  type AgreementQuery,
  type CalibrationAgreementReport,
  type CalibrationExport,
  type CalibrationItemForLabel,
  type CalibrationItemSummary,
  type CalibrationLabelSaved,
  type CalibrationSetCreated,
  type CalibrationSetDetail,
  type CalibrationSetSummary,
  type DrawBody,
  type VersionBody,
} from './labels';

const BASE = '/v1/admin/evals/calibration-sets';
const setPath = (setId: string) => `${BASE}/${encodeURIComponent(setId)}`;
const itemPath = (setId: string, itemId: string) =>
  `${setPath(setId)}/items/${encodeURIComponent(itemId)}/label`;
/** The explicit, audited reveal of a test-split item (ADR 0238). */
const reveal = (revealTest: boolean) => (revealTest ? '?revealTest=true' : '');

export const labelsApi = {
  async sets(): Promise<CalibrationSetSummary[]> {
    return (await adminApi<{ sets: CalibrationSetSummary[] }>(BASE)).sets;
  },
  async createSet(body: DrawBody): Promise<CalibrationSetCreated> {
    return (await adminApi<{ set: CalibrationSetCreated }>(BASE, { method: 'POST', body })).set;
  },
  async set(setId: string): Promise<CalibrationSetDetail> {
    return (await adminApi<{ set: CalibrationSetDetail }>(setPath(setId))).set;
  },
  async createVersion(setId: string, body: VersionBody): Promise<CalibrationSetCreated> {
    return (
      await adminApi<{ set: CalibrationSetCreated }>(`${setPath(setId)}/versions`, {
        method: 'POST',
        body,
      })
    ).set;
  },
  async items(setId: string): Promise<CalibrationItemSummary[]> {
    return (await adminApi<{ items: CalibrationItemSummary[] }>(`${setPath(setId)}/items`)).items;
  },
  /** One item to label: its case and reply; the verdict only once this admin has labelled it. */
  async item(setId: string, itemId: string, revealTest: boolean): Promise<CalibrationItemForLabel> {
    return (
      await adminApi<{ item: CalibrationItemForLabel }>(
        `${itemPath(setId, itemId)}${reveal(revealTest)}`
      )
    ).item;
  },
  /** Save this admin's label, once. The answer carries the judge's verdict. */
  saveLabel(
    setId: string,
    itemId: string,
    body: { label: boolean; note: string | null },
    revealTest: boolean
  ): Promise<CalibrationLabelSaved> {
    return adminApi<CalibrationLabelSaved>(`${itemPath(setId, itemId)}${reveal(revealTest)}`, {
      method: 'POST',
      body,
    });
  },
  async agreement(setId: string, q: AgreementQuery): Promise<CalibrationAgreementReport> {
    return (
      await adminApi<{ report: CalibrationAgreementReport }>(
        `${setPath(setId)}/agreement${agreementQuery(q)}`
      )
    ).report;
  },
  /** The frozen items for a re-grade; a set with test items needs the audited reveal. */
  exportSet(setId: string, revealTest: boolean): Promise<CalibrationExport> {
    return adminApi<CalibrationExport>(`${setPath(setId)}/export${reveal(revealTest)}`);
  },
};
